import { useEffect, useMemo, useRef, useState } from 'react';
import { DT } from '../../theme/designTokens';
import { COUNTRY_CATALOG, normalizeCountryCode, countryNameFor } from '../../config/countryCatalog';
import { CountryFlag } from './CountryFlag';
import { useTranslation } from '../../i18n/react';
import { localizedCountryName } from '../../i18n/format';

/**
 * Country Flag System V1 — reusable searchable country picker.
 *
 * Controlled component: `value` is the authoritative current selection
 * (validated lowercase code or null) and `onSelect` fires when the user
 * commits a choice — including null via the "Not selected" entry. The
 * caller owns persistence; on a failed save it simply keeps passing the
 * prior `value`, so the picker never shows an unconfirmed selection.
 *
 * Anatomy follows the app's existing anchored-dropdown precedent (Friends
 * AddFriend): a trigger button opens a panel with a search input and an
 * internally-scrolling listbox (scrollbar hidden, per app convention). No
 * native <select> and no third-party UI dependency. Combobox keyboard
 * behavior: ArrowUp/Down move, Enter commits, Escape closes, type to
 * filter by country name or two-letter code.
 */
export interface CountryPickerProps {
  value: string | null;
  onSelect: (code: string | null) => void;
  /** Disables interaction (e.g. while a save is in flight). */
  disabled?: boolean;
  id?: string;
  /**
   * Additive style override for the trigger button ONLY (Settings V1 —
   * DS2 white-field look). The dropdown panel, options, search, keyboard
   * behavior, and aria contract are identical in every variant.
   */
  triggerStyle?: React.CSSProperties;
}

const NOT_SELECTED_ID = 'country-option-none';

export function CountryPicker({ value, onSelect, disabled = false, id = 'country-picker', triggerStyle }: CountryPickerProps) {
  const { t, i18n } = useTranslation('settings');
  const current = normalizeCountryCode(value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Localized display list: codes stay the controlled/API value; names come
  // from Intl.DisplayNames (English catalog fallback), re-sorted for the
  // active locale with locale-aware comparison. Rebuilt only on language
  // change — a switch re-labels/re-sorts, never touches the selected code.
  const entries = useMemo(() => {
    let compare: (a: string, b: string) => number;
    try {
      compare = new Intl.Collator(i18n.language, { sensitivity: 'base' }).compare;
    } catch {
      compare = (a, b) => a.localeCompare(b);
    }
    return COUNTRY_CATALOG
      .map((entry) => ({
        code: entry.code,
        name: localizedCountryName(entry.code) ?? entry.name,
        englishName: entry.name,
      }))
      .sort((a, b) => compare(a.name, b.name));
  }, [i18n.language]);

  // A language change while the popover is open re-orders the list; reset
  // the keyboard cursor so it never silently points at a different country.
  useEffect(() => { setActiveIndex(0); }, [i18n.language]);

  // Search matches the localized name AND the English name (plus the code),
  // so switching languages never makes a country impossible to find.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (entry) =>
        entry.name.toLowerCase().includes(q) ||
        entry.englishName.toLowerCase().includes(q) ||
        entry.code.includes(q),
    );
  }, [entries, query]);

  // Options: "Not selected" first, then the filtered catalog.
  const optionCount = filtered.length + 1;

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => { setActiveIndex(0); }, [query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Keep the active option in view while arrowing through the list.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, open]);

  function commit(code: string | null) {
    setOpen(false);
    if (code !== current) onSelect(code);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, optionCount - 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(activeIndex === 0 ? null : filtered[activeIndex - 1]?.code ?? null);
    }
  }

  const currentName = current ? (localizedCountryName(current) ?? countryNameFor(current)) : null;
  const activeId = activeIndex === 0 ? NOT_SELECTED_ID : `country-option-${filtered[activeIndex - 1]?.code ?? 'none'}`;

  const optionBase: React.CSSProperties = {
    display:    'flex',
    alignItems: 'center',
    gap:        10,
    width:      '100%',
    padding:    '8px 12px',
    background: 'transparent',
    border:     'none',
    cursor:     'pointer',
    textAlign:  'left',
    fontFamily: DT.font.sans,
    fontSize:   14,
    color:      DT.color.textPrimary,
    WebkitTapHighlightColor: 'transparent',
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger — shows the authoritative current selection. */}
      <button
        type="button"
        id={id}
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('country.triggerAria', { name: currentName ?? t('country.notSelected') })}
        style={{
          display:      'inline-flex',
          alignItems:   'center',
          gap:          8,
          padding:      '7px 12px',
          background:   'rgba(255,255,255,0.04)',
          border:       `1px solid ${DT.color.border}`,
          borderRadius: 8,
          color:        currentName ? DT.color.textPrimary : DT.color.textMuted,
          fontFamily:   DT.font.sans,
          fontSize:     13,
          cursor:       disabled ? 'default' : 'pointer',
          opacity:      disabled ? 0.6 : 1,
          WebkitTapHighlightColor: 'transparent',
          ...triggerStyle,
        }}
      >
        <CountryFlag countryCode={current} width={24} decorative loading="eager" />
        <span>{currentName ?? t('country.notSelected')}</span>
        <span aria-hidden="true" style={{ fontSize: 10, color: DT.color.textMuted }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position:     'absolute',
            top:          'calc(100% + 6px)',
            right:        0,
            zIndex:       50,
            width:        280,
            background:   '#181818',
            border:       `1px solid ${DT.color.border}`,
            borderRadius: 10,
            boxShadow:    '0 12px 32px rgba(0,0,0,0.5)',
            overflow:     'hidden',
          }}
        >
          <div style={{ padding: 10, borderBottom: `1px solid ${DT.color.border}` }}>
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls={`${id}-listbox`}
              aria-activedescendant={activeId}
              aria-label={t('country.searchAria')}
              placeholder={t('country.searchPlaceholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              autoComplete="off"
              spellCheck={false}
              style={{
                width:        '100%',
                padding:      '8px 10px',
                background:   'rgba(255,255,255,0.05)',
                border:       `1px solid ${DT.color.border}`,
                borderRadius: 8,
                color:        DT.color.textPrimary,
                fontFamily:   DT.font.sans,
                fontSize:     13,
                outline:      'none',
                boxSizing:    'border-box',
              }}
            />
          </div>

          {/* Internally scrolling list — hidden scrollbar per app convention. */}
          <div
            ref={listRef}
            id={`${id}-listbox`}
            role="listbox"
            aria-label={t('country.listAria')}
            style={{
              maxHeight:      300,
              overflowY:      'auto',
              scrollbarWidth: 'none',
              padding:        '6px 0',
            }}
          >
            <button
              type="button"
              role="option"
              id={NOT_SELECTED_ID}
              aria-selected={current === null}
              data-active={activeIndex === 0 ? 'true' : undefined}
              onClick={() => commit(null)}
              onMouseEnter={() => setActiveIndex(0)}
              style={{
                ...optionBase,
                color:      DT.color.textMuted,
                fontStyle:  'italic',
                background: activeIndex === 0 ? 'rgba(255,255,255,0.08)' : 'transparent',
              }}
            >
              <CountryFlag countryCode={null} width={24} decorative loading="eager" />
              <span style={{ flex: 1 }}>{t('country.notSelected')}</span>
              {current === null && <span aria-hidden="true" style={{ color: DT.color.gold }}>✓</span>}
            </button>

            {filtered.map((entry, i) => {
              const idx = i + 1;
              const selected = entry.code === current;
              return (
                <button
                  key={entry.code}
                  type="button"
                  role="option"
                  id={`country-option-${entry.code}`}
                  aria-selected={selected}
                  data-active={activeIndex === idx ? 'true' : undefined}
                  onClick={() => commit(entry.code)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  style={{
                    ...optionBase,
                    background: activeIndex === idx
                      ? 'rgba(255,255,255,0.08)'
                      : selected ? 'rgba(201,168,76,0.10)' : 'transparent',
                  }}
                >
                  <CountryFlag countryCode={entry.code} width={24} decorative />
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.name}
                  </span>
                  {selected && <span aria-hidden="true" style={{ color: DT.color.gold }}>✓</span>}
                </button>
              );
            })}

            {filtered.length === 0 && (
              <div style={{ padding: '12px 14px', fontFamily: DT.font.sans, fontSize: 13, color: DT.color.textMuted }}>
                {t('country.noMatches', { query: query.trim() })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
